import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Form, Input, Button, Alert } from '@arco-design/web-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './useAuth.jsx';
import { ADMIN_ROLE_SLUGS } from '../common/helpers/roleLabels';
import { apiClient } from '@/services/apiClient';
import { API_ENDPOINTS } from '@/config/apiEndpoints';
import LanguageSwitcher from '@/modules/common/components/LanguageSwitcher';
import useRandomBackground from './useRandomBackground';
import './Auth.css';

const FormItem = Form.Item;

const CorporateLogin = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const { login, loading, error } = useAuth();
  const [formErrors, setFormErrors] = useState({});
  const { url: bgImage, credit } = useRandomBackground();

  const handleLoginSuccess = async (currentUser) => {
    const activeRole = currentUser?.active_profile?.role?.name;
    const hasAdminRole = activeRole
      ? ADMIN_ROLE_SLUGS.includes(activeRole)
      : currentUser?.profiles?.some(profile => profile.is_active && ADMIN_ROLE_SLUGS.includes(profile.role?.name));

    if (hasAdminRole) {
      navigate('/dashboard');
      return;
    }

    try {
      const response = await apiClient.get(API_ENDPOINTS.ONBOARDING_STATUS);
      const submissionStatus = response?.data?.status;
      if (submissionStatus === 'draft' || submissionStatus === 'changes_requested') {
        navigate('/onboarding/wizard');
      } else {
        navigate('/onboarding/status');
      }
    } catch (err) {
      console.error('Error al cargar estado de onboarding:', err);
      navigate('/onboarding/wizard');
    }
  };

  const handleSubmit = async (values) => {
    setFormErrors({});

    const email = (values.email || '').trim();
    const password = values.password || '';

    const errors = {};
    if (!email) errors.email = t('validation.emailRequired');
    else if (!/\S+@\S+\.\S+/.test(email)) errors.email = t('validation.emailInvalid');
    if (!password) errors.password = t('validation.passwordRequired');
    else if (password.length < 6) errors.password = t('validation.passwordMinLength6');

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const result = await login(email, password);
    if (result?.success) {
      await handleLoginSuccess(result.user);
    }
  };

  return (
    <div className="auth-container" style={{ backgroundImage: `url(${bgImage})` }}>
      {credit && (
        <span className="auth-photo-credit">
          Photo by{' '}
          <a href={`https://unsplash.com/photos/${credit.unsplash_photo_id}`} target="_blank" rel="noopener noreferrer">
            {credit.author}
          </a>
          {' / Unsplash'}
        </span>
      )}
      <div className="auth-card">
        <div className="auth-card-lang">
          <LanguageSwitcher />
        </div>
        <div className="auth-header">
          <h1>{t('corporateLogin.title')}</h1>
          <p>{t('corporateLogin.subtitle')}</p>
        </div>

        {error && (
          <Alert
            type="error"
            content={error}
            closable
            style={{ marginBottom: 20 }}
          />
        )}

        <Form layout="vertical" onSubmit={handleSubmit} autoComplete="off">
          <FormItem
            label={t('login.email')}
            validateStatus={formErrors.email ? 'error' : undefined}
            help={formErrors.email}
          >
            <Input
              name="email"
              placeholder={t('login.placeholderEmail')}
              onChange={() => formErrors.email && setFormErrors(prev => ({ ...prev, email: undefined }))}
            />
          </FormItem>
          <FormItem
            label={t('login.password')}
            validateStatus={formErrors.password ? 'error' : undefined}
            help={formErrors.password}
          >
            <Input.Password
              name="password"
              placeholder={t('login.placeholderPassword')}
              onChange={() => formErrors.password && setFormErrors(prev => ({ ...prev, password: undefined }))}
            />
          </FormItem>
          <FormItem>
            <Button
              type="primary"
              htmlType="submit"
              long
              loading={loading}
            >
              {t('login.submit')}
            </Button>
          </FormItem>
        </Form>

        <div className="auth-footer">
          <Link to="/login">{t('corporateLogin.backToLogin')}</Link>
        </div>
      </div>
    </div>
  );
};

export default CorporateLogin;
